// components/layout/Header.tsx - Fixed logo color in light mode

import React, { useEffect, useState, useRef } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { BrandLogo } from "@/components/layout";
import { User, Mail, Check, Menu, UserCircle, Trash2, AlertTriangle, Shield, CreditCard, Sun, Moon } from "lucide-react";
import { toast } from "@/hooks/use-toast";
import { useLoginModal } from "@/hooks/use-login-modal";
import { useWalletModal } from "@/hooks/use-wallet-modal";
import HistorySummary from "@/components/HistorySummary";
import { ProfileAvatar } from "@/components/ProfileAvatar";
import { useTheme } from "@/contexts/ThemeContext";

const NavItem = ({ to, label }: { to: string; label: string }) => {
  const location = useLocation();
  const active = location.pathname === to;
  return (
    <NavLink
      to={to}
      className={cn(
        "px-3 py-2 rounded-md text-sm font-medium transition-colors uppercase",
        active
          ? "text-gray-900 bg-gray-100 dark:text-white dark:bg-gray-800"
          : "text-gray-500 hover:text-gray-900 hover:bg-gray-100 dark:text-gray-400 dark:hover:text-white dark:hover:bg-gray-800"
      )}
    >
      {label}
    </NavLink>
  );
};

// Dropdown component for Trade menu
const TradeDropdown = ({ isOpen, onClose }: { isOpen: boolean; onClose: () => void }) => {
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        onClose();
      }
    };
    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return (
    <div ref={dropdownRef} className="absolute left-0 top-full mt-1 w-56 rounded-md border border-gray-200 dark:border-gray-800 bg-white dark:bg-background shadow-lg z-50 overflow-hidden">
      <Link
        to="/convert"
        className="block px-4 py-3 text-sm hover:bg-gray-50 dark:hover:bg-gray-900 transition-colors text-gray-900 dark:text-white"
        onClick={onClose}
      >
        <div className="font-medium uppercase tracking-wide">MARKET TRADE</div>
        <div className="text-[10px] text-gray-500 dark:text-gray-400 mt-0.5 uppercase tracking-wide">QUICK MARKET ORDERS</div>
      </Link>
      <Link
        to="/swap"
        className="block px-4 py-3 text-sm hover:bg-gray-50 dark:hover:bg-gray-900 transition-colors border-t border-gray-200 dark:border-gray-800 text-gray-900 dark:text-white"
        onClick={onClose}
      >
        <div className="font-medium uppercase tracking-wide">DEX SWAP</div>
        <div className="text-[10px] text-gray-500 dark:text-gray-400 mt-0.5 uppercase tracking-wide">DECENTRALIZED EXCHANGE SWAP</div>
      </Link>
      <Link
        to="/limit-order"
        className="block px-4 py-3 text-sm hover:bg-gray-50 dark:hover:bg-gray-900 transition-colors border-t border-gray-200 dark:border-gray-800 text-gray-900 dark:text-white"
        onClick={onClose}
      >
        <div className="font-medium uppercase tracking-wide">LIMIT ORDER</div>
        <div className="text-[10px] text-gray-500 dark:text-gray-400 mt-0.5 uppercase tracking-wide">SET PRICE TARGETS</div>
      </Link>
    </div>
  );
};

// Dropdown component for Assets menu
const AssetsDropdown = ({ isOpen, onClose }: { isOpen: boolean; onClose: () => void }) => {
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        onClose();
      }
    };
    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return (
    <div ref={dropdownRef} className="absolute left-0 top-full mt-1 w-56 rounded-md border border-gray-200 dark:border-gray-800 bg-white dark:bg-background shadow-lg z-50 overflow-hidden">
      <Link
        to="/assets"
        className="block px-4 py-3 text-sm hover:bg-gray-50 dark:hover:bg-gray-900 transition-colors text-gray-900 dark:text-white"
        onClick={onClose}
      >
        <div className="font-medium uppercase tracking-wide">MY PORTFOLIO</div>
        <div className="text-[10px] text-gray-500 dark:text-gray-400 mt-0.5 uppercase tracking-wide">VIEW ALL YOUR ASSETS</div>
      </Link>
      <Link
        to="/withdraw"
        className="block px-4 py-3 text-sm hover:bg-gray-50 dark:hover:bg-gray-900 transition-colors border-t border-gray-200 dark:border-gray-800 text-gray-900 dark:text-white"
        onClick={onClose}
      >
        <div className="font-medium uppercase tracking-wide">WITHDRAW</div>
        <div className="text-[10px] text-gray-500 dark:text-gray-400 mt-0.5 uppercase tracking-wide">SEND FUNDS TO EXTERNAL WALLET</div>
      </Link>
      <Link
        to="/transfer"
        className="block px-4 py-3 text-sm hover:bg-gray-50 dark:hover:bg-gray-900 transition-colors border-t border-gray-200 dark:border-gray-800 text-gray-900 dark:text-white"
        onClick={onClose}
      >
        <div className="font-medium uppercase tracking-wide">TRANSFER</div>
        <div className="text-[10px] text-gray-500 dark:text-gray-400 mt-0.5 uppercase tracking-wide">TRANSFER BETWEEN WALLETS</div>
      </Link>
    </div>
  );
};

export function Header() {
  const location = useLocation();
  const { isDarkMode, toggleTheme } = useTheme();
  const [currentUser, setCurrentUser] = useState<any | null>(null);
  const [kycStatus, setKycStatus] = useState<"none" | "pending" | "approved" | "rejected">("none");
  const [open, setOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [tradeDropdownOpen, setTradeDropdownOpen] = useState(false);
  const [assetsDropdownOpen, setAssetsDropdownOpen] = useState(false);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [loadingMethod, setLoadingMethod] = useState<string | null>(null);
  const [has2FA, setHas2FA] = useState<boolean>(false);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const ref = useRef<HTMLDivElement | null>(null);
  const menuRef = useRef<HTMLDivElement | null>(null);
  const tradeRef = useRef<HTMLDivElement | null>(null);
  const assetsRef = useRef<HTMLDivElement | null>(null);

  // Fetch user data using the session cookie (HttpOnly)
  const fetchUserFromSession = async (retryCount = 0) => {
    try {
      const response = await fetch("/api/auth", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
      });

      if (response.ok) {
        const data = await response.json();

        if (data.success && data.user) {
          setCurrentUser(data.user);
          setHas2FA(data.user.has2FA || false);
          await fetchKycStatus();
          setLoading(false);
        } else {
          if (retryCount < 1) {
            setTimeout(() => fetchUserFromSession(retryCount + 1), 100);
          } else {
            setCurrentUser(null);
            setLoading(false);
          }
        }
      } else if (response.status === 401) {
        setCurrentUser(null);
        setLoading(false);
      } else {
        setCurrentUser(null);
        setLoading(false);
      }
    } catch (error) {
      console.error("[Header] Session fetch error:", error);
      setCurrentUser(null);
      setLoading(false);
    }
  };

  const fetchKycStatus = async () => {
    try {
      const res = await fetch("/api/kyc/status", { credentials: "include" });
      const data = await res.json();
      if (data.status) {
        setKycStatus(data.status);
      }
    } catch (err) {
      console.error("[Header] Failed to fetch KYC status:", err);
    }
  };

  // Load user from API on mount
  useEffect(() => {
    fetchUserFromSession();

    const handleUserUpdated = () => {
      fetchUserFromSession();
    };

    window.addEventListener("userUpdated", handleUserUpdated);

    return () => {
      window.removeEventListener("userUpdated", handleUserUpdated);
    };
  }, []);

  // Helper function to clear any remaining client-side auth data (legacy)
  const clearLegacyAuthData = () => {
    sessionStorage.removeItem("userData");
    sessionStorage.removeItem("userEmail");
    sessionStorage.removeItem("authToken");
    sessionStorage.removeItem("sessionId");
    sessionStorage.removeItem("temp2FAData");
    
    localStorage.removeItem("currentUser");
    localStorage.removeItem("authToken");
    localStorage.removeItem("userEmail");
    localStorage.removeItem("has2FA");
    localStorage.removeItem("sessionId");
    localStorage.removeItem("userData");
    localStorage.removeItem("user");
    localStorage.removeItem("trustedDevice");
    localStorage.removeItem("deviceEmail");
    
    document.cookie = "session=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;";
  };

  useEffect(() => {
    function onDoc(e: MouseEvent) {
      if (!ref.current) return;
      if (e.target && ref.current.contains(e.target as Node)) return;
      setOpen(false);

      if (!menuRef.current) return;
      if (e.target && menuRef.current.contains(e.target as Node)) return;
      setMobileMenuOpen(false);
    }
    document.addEventListener("click", onDoc);
    return () => document.removeEventListener("click", onDoc);
  }, []);

  // Close dropdowns when route changes
  useEffect(() => {
    setTradeDropdownOpen(false);
    setAssetsDropdownOpen(false);
  }, [location.pathname]);

  async function signOut() {
    try {
      await fetch("/api/logout", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
      });
    } catch (error) {
      console.error("Logout error:", error);
    }
    
    clearLegacyAuthData();
    setCurrentUser(null);
    setOpen(false);
    setMobileMenuOpen(false);
    
    window.dispatchEvent(new CustomEvent("userUpdated"));
    
    toast({
      title: "LOGGED OUT",
      description: "YOU HAVE BEEN SUCCESSFULLY LOGGED OUT.",
    });
    
    navigate("/");
  }

  async function handleProfileImageUpload(event: React.ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0];
    if (!file) return;

    setLoadingMethod("uploadImage");
    try {
      const reader = new FileReader();
      reader.onload = async (e) => {
        const base64String = e.target?.result as string;

        const updated = {
          ...currentUser,
          selfie_image: base64String,
        };
        
        setCurrentUser(updated);
        
        window.dispatchEvent(new CustomEvent("userUpdated"));

        if (currentUser?.id) {
          try {
            await fetch(`/api/user/${currentUser.id}`, {
              method: "PATCH",
              headers: { "Content-Type": "application/json" },
              credentials: "include",
              body: JSON.stringify({ selfie_image: base64String }),
            });
          } catch (err) {
            console.error("Failed to update profile image:", err);
          }
        }

        toast({
          title: "SUCCESS",
          description: "PROFILE IMAGE UPDATED SUCCESSFULLY",
        });
      };
      reader.readAsDataURL(file);
    } catch (error) {
      toast({
        title: "ERROR",
        description: "FAILED TO UPLOAD IMAGE",
        variant: "destructive",
      });
    } finally {
      setLoadingMethod(null);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  }

  if (loading) {
    return (
      <header className="sticky top-0 z-40 border-b border-gray-200 dark:border-gray-800 bg-white dark:bg-background">
        <div className="container flex md:h-16 h-14 items-center justify-between">
          <Link to="/" className="flex-shrink-0 text-black dark:text-white">
            <BrandLogo />
          </Link>
          <div className="w-8 h-8 rounded-full bg-gray-100 dark:bg-gray-800 animate-pulse"></div>
        </div>
      </header>
    );
  }

  if (currentUser) {
    const isKycVerified = kycStatus === "approved";

    return (
      <>
        <header className="sticky top-0 z-40 border-b border-gray-200 dark:border-gray-800 bg-white dark:bg-background">
          <div className="container flex md:h-16 h-14 items-center justify-between gap-4">
            {/* Logo */}
            <Link to="/" className="flex-shrink-0 text-black dark:text-white">
              <BrandLogo />
            </Link>

            {/* Desktop nav with dropdowns */}
            <nav className="hidden md:flex items-center gap-1">
              <NavItem to="/" label="HOME" />
              
              {/* Trade Dropdown */}
              <div className="relative" ref={tradeRef}>
                <button
                  onClick={() => {
                    setTradeDropdownOpen(!tradeDropdownOpen);
                    setAssetsDropdownOpen(false);
                  }}
                  className={cn(
                    "px-3 py-2 rounded-md text-sm font-medium transition-colors uppercase",
                    tradeDropdownOpen || location.pathname === "/convert" || location.pathname === "/swap" || location.pathname === "/limit-order"
                      ? "text-gray-900 bg-gray-100 dark:text-white dark:bg-gray-800"
                      : "text-gray-500 hover:text-gray-900 hover:bg-gray-100 dark:text-gray-400 dark:hover:text-white dark:hover:bg-gray-800"
                  )}
                >
                  TRADE
                </button>
                <TradeDropdown isOpen={tradeDropdownOpen} onClose={() => setTradeDropdownOpen(false)} />
              </div>

              {/* Assets Dropdown */}
              <div className="relative" ref={assetsRef}>
                <button
                  onClick={() => {
                    setAssetsDropdownOpen(!assetsDropdownOpen);
                    setTradeDropdownOpen(false);
                  }}
                  className={cn(
                    "px-3 py-2 rounded-md text-sm font-medium transition-colors uppercase",
                    assetsDropdownOpen || location.pathname === "/assets" || location.pathname === "/withdraw" || location.pathname === "/transfer"
                      ? "text-gray-900 bg-gray-100 dark:text-white dark:bg-gray-800"
                      : "text-gray-500 hover:text-gray-900 hover:bg-gray-100 dark:text-gray-400 dark:hover:text-white dark:hover:bg-gray-800"
                  )}
                >
                  ASSET
                </button>
                <AssetsDropdown isOpen={assetsDropdownOpen} onClose={() => setAssetsDropdownOpen(false)} />
              </div>

              <NavItem to="/p2p" label="EXPRESS" />
              <NavItem to="/history" label="HISTORY" />
              <NavItem to="/reward" label="CLAIM FREE REWARD" />
            </nav>

            {/* History Display for Desktop */}
            <div className="hidden md:block px-4">
              <HistorySummary variant="compact" className="border-l border-r border-gray-200 dark:border-gray-800 pl-4 pr-4" />
            </div>

            {/* User dropdown with hamburger trigger */}
            <div className="flex items-center gap-2 ml-auto relative" ref={ref}>
              <button
                onClick={() => setOpen((s) => !s)}
                className="flex flex-col justify-center items-center gap-1 w-8 h-8 p-1 rounded-md border border-gray-200 dark:border-gray-700 hover:bg-gray-100 dark:hover:bg-gray-800"
                title="USER MENU"
              >
                <span className="block w-5 h-[2px] bg-gray-900 dark:bg-white rounded"></span>
                <span className="block w-5 h-[2px] bg-gray-900 dark:bg-white rounded"></span>
                <span className="block w-5 h-[2px] bg-gray-900 dark:bg-white rounded"></span>
              </button>

              {open && (
                <div className="absolute right-0 top-full mt-2 w-80 rounded-md border border-gray-200 dark:border-gray-800 bg-white dark:bg-background p-4 shadow-lg z-50 origin-top-right">
                  <div className="flex items-center gap-3 mb-4 pb-4 border-b border-gray-200 dark:border-gray-800">
                    <UserCircle className="w-12 h-12 text-gray-400 dark:text-gray-500 flex-shrink-0" />
                    <div className="flex-1">
                      <div className="font-semibold text-sm text-gray-900 dark:text-white uppercase">
                        {currentUser.name || currentUser.email?.split('@')[0] || "MEMBER"}
                      </div>
                      <div className="text-[10px] text-gray-500 dark:text-gray-400 truncate uppercase">
                        {currentUser.email}
                      </div>
                    </div>
                  </div>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    onChange={handleProfileImageUpload}
                    disabled={loadingMethod === "uploadImage"}
                    className="hidden"
                    aria-label="Upload profile image"
                  />
                  <div className="mt-3 text-[10px] text-gray-500 dark:text-gray-400">
                    <div className="uppercase">
                      MEMBER ID:{" "}
                      <span className="inline-block border border-gray-200 dark:border-gray-800 px-2 py-0.5 rounded font-mono text-[10px] text-gray-900 dark:text-white">
                        {currentUser.id}
                      </span>
                    </div>
                  </div>

                  {/* 2FA Status */}
                  <div className="mt-3 border-t border-gray-200 dark:border-gray-800 pt-3">
                    <div className="text-xs">
                      {has2FA || currentUser.twoFactorEnabled ? (
                        <div className="bg-green-50 dark:bg-green-950 p-2 rounded text-green-700 dark:text-green-400 flex items-center gap-2 uppercase">
                          <Check className="w-3 h-3" />
                          2FA ENABLED
                        </div>
                      ) : (
                        <div className="bg-amber-50 dark:bg-amber-950 p-2 rounded text-amber-700 dark:text-amber-400 flex items-center gap-2 uppercase">
                          <Check className="w-3 h-3" />
                          2FA DISABLED
                        </div>
                      )}
                    </div>
                  </div>

                  {/* KYC Status with Verify link */}
                  <div className="mt-2">
                    <div className="text-xs flex items-center justify-between">
                      {isKycVerified ? (
                        <div className="bg-green-50 dark:bg-green-950 p-2 rounded text-green-700 dark:text-green-400 flex items-center gap-2 flex-1 uppercase">
                          <Check className="w-3 h-3" />
                          KYC VERIFIED
                        </div>
                      ) : (
                        <>
                          <div className="bg-amber-50 dark:bg-amber-950 p-2 rounded text-amber-700 dark:text-amber-400 flex items-center gap-2 flex-1 uppercase">
                            <AlertTriangle className="w-3 h-3" />
                            KYC UNVERIFIED
                          </div>
                          <Link
                            to="/kyc"
                            className="ml-2 text-[10px] font-semibold text-yellow-600 hover:text-yellow-700 dark:text-yellow-400 whitespace-nowrap uppercase"
                          >
                            VERIFY
                          </Link>
                        </>
                      )}
                    </div>
                  </div>

                  {/* Theme Toggle */}
                  <div className="mt-3 border-t border-gray-200 dark:border-gray-800 pt-3">
                    <button
                      onClick={toggleTheme}
                      className="w-full flex items-center justify-between px-3 py-2 rounded-md hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors text-sm text-gray-700 dark:text-gray-300 uppercase"
                    >
                      <span className="flex items-center gap-2">
                        {isDarkMode ? <Moon className="w-4 h-4" /> : <Sun className="w-4 h-4" />}
                        {isDarkMode ? "DARK MODE" : "LIGHT MODE"}
                      </span>
                      <span className="text-xs text-gray-400 dark:text-gray-500">
                        {isDarkMode ? "🌙" : "☀️"}
                      </span>
                    </button>
                  </div>

                  {/* SIGN OUT Button */}
                  <div className="mt-3 grid gap-2">
                    <button
                      onClick={signOut}
                      className="w-full rounded-md border border-gray-200 dark:border-gray-800 px-3 py-2 text-xs hover:bg-gray-50 dark:hover:bg-gray-800 bg-red-50 dark:bg-red-950/30 text-red-600 dark:text-red-400 hover:bg-red-100 dark:hover:bg-red-900/50 uppercase font-bold"
                    >
                      SIGN OUT
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </header>
      </>
    );
  }

  const mobileMenuItems = [
    { label: "COMPLETE LOGIN", to: "/" },
  ];

  return (
    <header className="sticky top-0 z-40 border-b border-gray-200 dark:border-gray-800 bg-white dark:bg-background">
      <div className="container flex md:h-16 h-14 items-center justify-between gap-4">
        <Link to="/" className="flex-shrink-0 text-black dark:text-white">
          <BrandLogo />
        </Link>

        {/* Mobile: Hamburger menu */}
        <div className="md:hidden ml-auto relative" ref={menuRef}>
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-md transition-colors"
            aria-label="TOGGLE MENU"
          >
            <Menu className="h-6 w-6 text-gray-900 dark:text-white" />
          </button>

          {mobileMenuOpen && (
            <div className="absolute right-0 top-full mt-2 w-48 bg-white dark:bg-background border border-gray-200 dark:border-gray-800 rounded-md shadow-lg z-50">
              {mobileMenuItems.map((item) =>
                item.to ? (
                  <Link
                    key={item.label}
                    to={item.to}
                    className="block px-4 py-2 text-sm font-medium hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors first:rounded-t-md last:rounded-b-md text-gray-900 dark:text-white uppercase"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    {item.label}
                  </Link>
                ) : (
                  <a
                    key={item.label}
                    href={item.href}
                    className="block px-4 py-2 text-sm font-medium hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors first:rounded-t-md last:rounded-b-md text-gray-900 dark:text-white uppercase"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    {item.label}
                  </a>
                )
              )}
            </div>
          )}
        </div>

        {/* Desktop nav */}
        <nav className="hidden md:flex items-center gap-1">
          <NavItem to="/" label="HOME" />
          <NavItem to="/markets" label="TRADE" />
          <NavItem to="/assets" label="ASSET" />
          <NavItem to="/p2p" label="P2P" />
        </nav>

        {/* Desktop auth buttons */}
        <div className="hidden sm:flex items-center gap-3 ml-auto">
          <Button
            asChild
            variant="ghost"
            className="hidden sm:inline-flex text-gray-500 hover:text-gray-900 hover:bg-gray-100 dark:text-gray-400 dark:hover:text-white dark:hover:bg-gray-800 uppercase"
          >
            <Link to="/login">LOG IN</Link>
          </Button>
          <Button
            asChild
            className="bg-teal-500 hover:bg-teal-600 text-white rounded-lg dark:bg-teal-600 dark:hover:bg-teal-700 uppercase"
          >
            <Link to="/signup">SIGN UP</Link>
          </Button>
        </div>
      </div>
    </header>
  );
}

// Add default export to fix the import issue
export default Header;
